import FilterEditorItem from './FilterEditorItem';
import Filter from './Filter';
import { _ } from '../../Lang.class.js';
import SelectedArea from './SelectedArea';
// import OverlayLayer from "../OverlayLayer/OverlayLayer";
import type { API_ObjectAttributesType } from '../../types-telematics';
import type { GeojsonFieldsType, QueriesType, QueryGeometryType, QueryType } from '../../types';
import type { FilterSubMenuValue, RelationType } from '../../types-analytics';
import LabelMenuElement from './LabelMenuElement';
import FilterLastUsed from './FilterLastUsed';
import { featureMakeMarkedSelectedFilterDescription } from './featureMakeMarkedSelectedFilterDescription';
import { WindowEffect } from '../WindowEffect/WindowEffect';
import OverlayLayer from '../OverlayLayer/OverlayLayer';
import type { FilterManager } from './FilterManager';

interface ModalFilterEditorCallbacks {
    onSave: (filter: Filter, filterLastUsed: Filter) => void;
    onCancel: () => void;
    onLoadView: () => void;
    onSaveView: () => void;
    onHide: () => void;
    onTemporaryHide: () => void;
}

class ModalFilterEditor {
    private root: HTMLElement;
    private originalFilter: Filter;
    private filterLastUsed: FilterLastUsed;
    private filter: Filter;
    private availableTypes: API_ObjectAttributesType[];
    private callbacks: ModalFilterEditorCallbacks;
    private szumDrawer: any;
    private selectedKey: string | null;
    private overlay: any;
    private handlerOnClickOutside: Function | null;
    private filterEditorItems: { [key: string]: FilterEditorItem } = {};
    private relativeTargetRect: HTMLElement;
    private showViewButtons: boolean;
    private dirtyStatus: { isChanged: boolean; key: string | null } = {
        isChanged: false,
        key: null,
    };
    private windowEffect: WindowEffect;
    // private filterManager: FilterManager;

    constructor(
        root: HTMLElement,
        originalFilter: Filter,
        filterLastUsed: FilterLastUsed,
        szumDrawer: any,
        relativeTargetRect: HTMLElement,
        showViewButtons: boolean,
        // filterManager: FilterManager,
        callbacks: ModalFilterEditorCallbacks
    ) {
        // console.log('ModalFilterEditor, constructor, originalFilter', originalFilter);
        this.root = root;
        // this.root.style.display = 'none';
        // this.windowEffect = new WindowEffect(this.root as HTMLDivElement, 'block');

        this.originalFilter = originalFilter;
        this.filterLastUsed = filterLastUsed;

        this.relativeTargetRect = relativeTargetRect;

        this.showViewButtons = showViewButtons;

        this.filter = new Filter(); // create new instance of Filter (needed to replace or drop changes)
        let filters = originalFilter.getFilterQueries();
        for (let key in filters) {
            this.filter.updateFilterItem(key, filters[key]);
        }
        this.availableTypes = originalFilter.getAvailableTypes();
        this.filter.setAvailableValuesAll(originalFilter.getAvailableValuesAll());
        this.filter.setFilterRelated(originalFilter.getFilterRelated());
        this.filter.andOrSelection = originalFilter.andOrSelection;
        this.filter.andOrSelectionEnabled = originalFilter.andOrSelectionEnabled;
        this.callbacks = callbacks;

        // get random string for overlay id
        // https://stackoverflow.com/questions/1349404/generate-random-string-characters-in-javascript
        this.overlay = OverlayLayer(
            'ModalFilterEditor',
            {
                onClick: () => {
                    if (
                        this.selectedKey &&
                        this.filterEditorItems[this.selectedKey] &&
                        typeof this.filterEditorItems[this.selectedKey].onClose == 'function' // is callback defined?
                    ) {
                        this.filterEditorItems[this.selectedKey].onClose(this.filter);
                    }
                    this.hide();
                },
            },
            // 20099
            1001
        ); // 20000 // FIXME:

        // console.log('originalFilter.getAvailableValuesAll()', originalFilter.getAvailableValuesAll());
        this.selectedKey = null;

        this.handlerOnClickOutside = null;

        this.szumDrawer = szumDrawer;

        this.reload();

        let dialog = this.root.querySelector('dialog');
        if (dialog) {
            window.onclick = event => {
                if (event.target === this.root.querySelector('dialog')) {
                    dialog.close();
                }
            };

            dialog.querySelector('.modal-filter-editor')?.addEventListener('click', event => {
                event.stopPropagation();
            });
        }
    }

    onChangeAnything(key: string) {
        this.dirtyStatus.isChanged = true;
        this.dirtyStatus.key = key;
    }

    clearDirtyStatus() {
        this.dirtyStatus.isChanged = false;
        this.dirtyStatus.key = null;
    }

    show(
        getArrowTarget: HTMLElement,
        showOnlySelectedRule: boolean = false,
        reload: boolean = true
    ): void {
        // console.log('ModalFilterEditor show()');

        this.root.style.display = 'block';
        // this.windowEffect.show();

        if (reload) {
            this.reload(showOnlySelectedRule);
        }

        if (showOnlySelectedRule) {
            (this.root.querySelector('.filter_list_title_list') as HTMLDivElement).style.display =
                'none';
            let backgroundColor = window.getComputedStyle(
                this.root.querySelector('.filter_list_columns') as HTMLDivElement
            ).backgroundColor;
            (
                this.root.querySelector('.container__arrow.container__arrow--tl') as HTMLDivElement
            ).style.backgroundColor = backgroundColor;
        } else {
            (this.root.querySelector('.filter_list_title_list') as HTMLDivElement).style.display =
                'flex';
            (
                this.root.querySelector('.container__arrow.container__arrow--tl') as HTMLDivElement
            ).style.backgroundColor = (
                this.root.querySelector('.filter_list_title_list') as HTMLDivElement
            ).style.backgroundColor;
        }

        // this.root.querySelector('dialog').showModal();

        // this.setModalPosition(getArrowTarget);
        // window.onresize = (event) => {
        //     this.setModalPosition(getArrowTarget);
        // };

        this.overlay.show();
    }

    hide(): void {
        // console.log('parentRects, hide');
        this.root.style.display = 'none';
        // this.windowEffect.hide();
        featureMakeMarkedSelectedFilterDescription.clearSelection();
        // this.root.querySelector('dialog').close();
        this.overlay.hide();
        this.callbacks.onHide();
    }

    setSelectedRule(ruleKey) {
        this.selectedKey = ruleKey;
    }

    validate(): boolean {
        // console.log('ModalFilterEditor, validate');
        return true;
    }

    setFilter(f: QueriesType): void {
        this.originalFilter.setFilterQueries(f);
        this.filter.setFilterQueries({});
        for (let key in f) {
            this.filter.updateFilterItem(key, f[key]);
        }
        console.log('ModalFilterEditor, setFilter', f);
    }

    reload(showOnlySelectedRule: boolean = false): void {
        this.root.innerHTML = this.getHTML();

        // Set event when user click outside of the modal, then cancel all changes and close window
        if (this.handlerOnClickOutside === null) {
            this.handlerOnClickOutside = e => {
                if (e.target === this.root) {
                    if (
                        this.selectedKey &&
                        this.filterEditorItems[this.selectedKey] &&
                        typeof this.filterEditorItems[this.selectedKey].onClose == 'function' // is callback defined?
                    ) {
                        this.filterEditorItems[this.selectedKey].onClose(this.filter);
                    }
                    // this.root.close();
                    this.hide();
                    let dirty = false;
                    let filters = this.filter.getFilterQueries();
                    for (let key in filters) {
                        if (filters[key] != this.originalFilter.getFilterItem(key)) {
                            dirty = true;
                            break;
                        }
                    }
                    if (!dirty) {
                        filters = this.originalFilter.getFilterQueries();
                        for (let key in filters) {
                            if (filters[key] != this.filter.getFilterItem(key)) {
                                dirty = true;
                                break;
                            }
                        }
                    }
                    if (dirty) {
                        this.callbacks.onSave(this.filter, this.filterLastUsed);
                    } else {
                        this.callbacks.onCancel();
                    }
                }
            };
            this.root.addEventListener('click', this.handlerOnClickOutside as any);
            this.root.addEventListener('cancel', this.handlerOnClickOutside as any);
        }

        const filter_list_title_list = this.root.querySelector(
            '.filter_list_title_list'
        ) as HTMLDivElement;
        if (filter_list_title_list) {
            this.populateLastUsedFilters(filter_list_title_list);
            let columnNr = 0;
            this.originalFilter.getFilterColumns().order.forEach(columnKey => {
                columnNr++;
                try {
                    this.addFilterColumn(
                        filter_list_title_list,
                        columnKey,
                        this.originalFilter.getFilterColumns().column[columnKey].label,
                        columnNr > 1,
                        showOnlySelectedRule
                    );
                } catch (e) {
                    console.error(e);
                    console.error('Not found columnKey in column data: ', columnKey);
                    console.table(this.originalFilter.getFilterColumns());
                    console.table(this.originalFilter.getFilterGroups());
                }
            });
        } else {
            throw new Error('filter_list_title_list is null');
        }

        this.clearDirtyStatus();
        // this.populateFilterLabelsAndEvents();
    }

    addFilterColumn(
        filter_list_title_list: HTMLDivElement,
        columnKey: string,
        columnName: string,
        withLeftBorder: boolean,
        showOnlySelectedRule: boolean
    ): void {
        const column = this.originalFilter.getFilterColumns().column[columnKey];
        const column_groups = column.grp;

        let one_column = document.createElement('div');
        one_column.setAttribute(
            'class',
            `filter_group_box ${withLeftBorder ? 'with_left_border' : ''}`
        );

        let column_header = document.createElement('div');
        column_header.setAttribute('class', 'menu_filter_column_name');
        let category_translated = _(columnName);
        column_header.innerHTML =
            category_translated.charAt(0).toUpperCase() + category_translated.slice(1);
        one_column.appendChild(column_header);

        let group_container = document.createElement('div');
        group_container.setAttribute('class', 'filter_column_group_container');
        one_column.appendChild(group_container);

        column_groups.forEach(groupKey => {
            let groupElement = document.createElement('div');
            groupElement.setAttribute('class', 'filter_column_group');
            groupElement.setAttribute('data-group', groupKey);
            groupElement.setAttribute('data-column', columnKey);
            groupElement.innerHTML =
                '<div class="menu_filter_group_name"><span>' +
                _(this.originalFilter.getFilterGroups().group[groupKey].label) +
                '</span></div>';

            let groupElementsContainer = document.createElement('div');
            groupElementsContainer.setAttribute('class', 'filter_column_group_elements');
            // groupElementsContainer.style.scroll = 'thin';
            groupElementsContainer.style.overflowY = 'auto';
            groupElement.appendChild(groupElementsContainer);

            this.populateFilterGroup(
                groupElementsContainer,
                this.originalFilter.getFilterGroups().group[groupKey].fld,
                showOnlySelectedRule
            );

            group_container.appendChild(groupElement);
        });

        if (filter_list_title_list) {
            filter_list_title_list.appendChild(one_column);
        } else {
            throw new Error('filter_list_title_list is null');
        }
    }

    isLongKeyUsed(used: boolean, attrkey: string, key: string): boolean {
        if (used && key.indexOf('/') >= 0) {
            switch (attrkey) {
                case 'geom':
                    used =
                        attrkey +
                            '/' +
                            (this.filter.getFilterItem('geom')?.properties?.filter ?? 'area') ==
                        key;
                    break;
            }
        }
        return used;
        // if (used && key.indexOf('/') >= 0) {
        //     switch (attrkey) {
        //         case 'geom':
        //             used = attrkey + '/' + (this.filter.getFilterItem('geom')['$geoWithin']['$geometry']?.properties?.filter ?? 'area') == key;
        //             break;
        //     }
        // }
    }

    populateFilterGroup(
        container: HTMLElement,
        fields: string[],
        showOnlySelectedRule: boolean = false
    ) {
        fields.forEach(attrkey => {
            if (this.originalFilter.getFilterableForItemKey(attrkey)) {
                let subMenu: FilterSubMenuValue = this.originalFilter.getSubMenuItems(attrkey);

                // FIXME: Reduce this code duplication, when we have time
                if (subMenu.items.length > 0) {
                    // Create label (when user clicks on it, it will show/hide the submenu)
                    container.appendChild(
                        new LabelMenuElement().getLabelMenuElement(
                            attrkey,
                            subMenu.label.charAt(0).toUpperCase() + subMenu.label.slice(1),
                            this.selectedKey === attrkey,
                            subMenu.items.some(item => this.filter.isKeyUsed(item.id)),
                            true,
                            showOnlySelectedRule,
                            {
                                onLabelClick: (key: string) => {
                                    // this.filterEditorItems[key].load(this.filter);
                                    if (this.onLabelClickConfirmed(key, this.filter)) {
                                        if (typeof this.filterEditorItems[key] !== 'undefined') {
                                            this.filterEditorItems[key].reload();
                                        } else {
                                            this.filterEditorItems[subMenu.items[0].id].reload();
                                        }
                                        return true;
                                    } else {
                                        return false;
                                    }
                                },
                                reload: () => {
                                    // this.filterEditorItems[key].reload();
                                    // alert('reload, LabelMenuElement');
                                },
                            }
                        )
                    );

                    // console.log('subMenu.items', subMenu.items);

                    // let filtersForKey = this.filter.getMoreKeysForThisKey(attrkey);
                    subMenu.items.forEach((subMenuItem: { id: string; name: string }) => {
                        let key = subMenuItem.id;
                        let used = this.filter.isKeyUsed(attrkey);
                        // used = this.isLongKeyUsed(used, attrkey, key);
                        if (used && key.indexOf('/') >= 0) {
                            switch (attrkey) {
                                case 'geom':
                                    let geom = this.filter.getFilterItem('geom');
                                    try {
                                        used =
                                            attrkey + '/' + (geom?.properties?.filter ?? 'area') ==
                                            key;
                                    } catch (e) {
                                        console.warn(e?.message);
                                    }
                                    if (geom?.properties?.filter) {
                                        used =
                                            attrkey +
                                                '/' +
                                                (geom['properties']['filter'] ?? 'radius') ==
                                            key;
                                    }
                                    break;
                            }
                        }

                        this.filterEditorItems[key] = new FilterEditorItem(
                            this.root.querySelector('.filter_list_columns'),
                            this.filter,
                            key,
                            this.selectedKey === key,
                            this.filter.isKeyUsed(subMenuItem.id),
                            subMenu,
                            showOnlySelectedRule,
                            {
                                label:
                                    subMenu.label.charAt(0).toUpperCase() + subMenu.label.slice(1),
                                info: this.availableTypes
                                    .filter(item => item.name == key)
                                    .map(item => item.info)[0],
                                mongoCondition: this.filter.getFilterItem(key),
                                dataType: this.availableTypes
                                    .filter(item => item.name == key)
                                    .map(item => item.data_type)[0],
                                availableValues: this.filter.getAvailableValues(key),
                                displayMethod: this.originalFilter.getFieldData(key).displayMethod,
                                unit: this.availableTypes
                                    .filter(item => item.name == key)
                                    .map(item => item.unit)[0],
                                maxLength:
                                    this.availableTypes
                                        .filter(item => item.name == key)
                                        .map(item => item.max_length)[0] ?? 0,
                                min:
                                    this.availableTypes
                                        .filter(item => item.name == key)
                                        .map(item => item.min)[0] ?? null,
                                max:
                                    this.availableTypes
                                        .filter(item => item.name == key)
                                        .map(item => item.max)[0] ?? null,
                                period_range:
                                    this.availableTypes
                                        .filter(item => item.name == key)
                                        .map(item => item.period_range)[0] ?? null,
                                collection:
                                    this.availableTypes
                                        .filter(item => item.name == key)
                                        .map(item => item.collection)[0] ?? null,
                                fld_period:
                                    this.availableTypes
                                        .filter(item => item.name == key)
                                        .map(item => item.fld_period)[0] ?? null,
                                default_values: this.availableTypes
                                    .filter(item => item.name == key)
                                    .map(item => item.default_values)[0] ?? [-25, -25],
                            },
                            this.szumDrawer,
                            showOnlySelectedRule, //this.filter.isKeyUsed(subMenuItem.id), // can be removed if is used
                            {
                                onSave: (key: string, mongoCondition, andOrSelection: string) => {
                                    // this.filterManager.featureInactiveQueries.setActiveQuery(key, true);
                                    this.onItemSave(key, mongoCondition, andOrSelection);
                                },
                                onSaveRelatedFields: (key: string, relatedFields) => {
                                    this.onItemRelatedFieldsSave(key, relatedFields);
                                },
                                onRemove: (key: string) => {
                                    this.onItemRemove(key);
                                },
                                onChangeSubMenu: (key: string) => {
                                    console.log(
                                        'onChangeSubMenu, populateFilterGroup, submenu',
                                        key
                                    );
                                    this.filterEditorItems[key].reload();
                                },
                                onTemporaryHideModal: () => {
                                    console.log('onTemporaryHideModal');
                                    this.hide();
                                },
                                onShowModal: () => {
                                    this.show(
                                        document.querySelector('.js-add-filter'),
                                        false,
                                        false
                                    );
                                },
                                onDrawComplete: (key, geojson) => {
                                    this.onDrawComplete(key, geojson);
                                },
                                onDrawCancel: key => {
                                    this.filterEditorItems[key].onClose(this.filter);
                                    this.callbacks.onCancel();
                                },
                                onChangeAnything: (key: string) => {
                                    this.onChangeAnything(key);
                                },
                            }
                        );
                    });
                } else {
                    let filtersForKey = this.filter.getMoreKeysForThisKey(attrkey);
                    for (let i = 0; i < filtersForKey.length; i++) {
                        let key = filtersForKey[i];

                        if (this.originalFilter.isKeyUsedInSubMenu(key)) {
                            // console.info('This key ', key, ' is used in submenu, so we skip it here.');
                            continue;
                        }

                        let itemLabel = _(this.originalFilter.getNameForItemKey(key));
                        let used = this.filter.isKeyUsed(attrkey);
                        // used = this.isLongKeyUsed(used, attrkey, key);
                        if (used && key.indexOf('/') >= 0) {
                            switch (attrkey) {
                                case 'geom':
                                    let geom = this.filter.getFilterItem('geom');
                                    try {
                                        used =
                                            attrkey + '/' + (geom?.properties?.filter ?? 'area') ==
                                            key;
                                    } catch (e) {
                                        console.warn(e?.message);
                                    }
                                    if (geom?.properties?.filter) {
                                        used =
                                            attrkey +
                                                '/' +
                                                (geom['properties']['filter'] ?? 'radius') ==
                                            key;
                                    }
                                    break;
                            }
                        }

                        container.appendChild(
                            new LabelMenuElement().getLabelMenuElement(
                                key,
                                itemLabel.charAt(0).toUpperCase() + itemLabel.slice(1),
                                this.selectedKey === key,
                                used,
                                false,
                                showOnlySelectedRule,
                                {
                                    onLabelClick: (key: string) => {
                                        console.log(
                                            '@emptyField onLabelClick, populateFilterGroup, LabelMenuElement, item',
                                            key,
                                            this.filter.getFilterQueries()
                                        );
                                        if (this.onLabelClickConfirmed(key, this.filter)) {
                                            this.filterEditorItems[key].load(this.filter);
                                            this.filterEditorItems[key].reload();
                                            return true;
                                        } else {
                                            return false;
                                        }
                                    },
                                    reload: () => {
                                        this.filterEditorItems[key].reload();
                                    },
                                }
                            )
                        );

                        this.filterEditorItems[key] = new FilterEditorItem(
                            this.root.querySelector('.filter_list_columns'),
                            this.filter,
                            key,
                            this.selectedKey === key,
                            used,
                            subMenu,
                            showOnlySelectedRule,
                            {
                                // make first letter uppercase in label
                                label: itemLabel.charAt(0).toUpperCase() + itemLabel.slice(1),
                                info: this.availableTypes
                                    .filter(item => item.name == attrkey)
                                    .map(item => item.info)[0],
                                mongoCondition: this.filter.getFilterItem(attrkey),
                                dataType: this.availableTypes
                                    .filter(item => item.name == attrkey)
                                    .map(item => item.data_type)[0],
                                availableValues: this.filter.getAvailableValues(attrkey),
                                displayMethod: this.originalFilter.getFieldData(key).displayMethod,
                                unit: this.availableTypes
                                    .filter(item => item.name == attrkey)
                                    .map(item => item.unit)[0],
                                maxLength:
                                    this.availableTypes
                                        .filter(item => item.name == attrkey)
                                        .map(item => item.max_length)[0] ?? 0,
                                min:
                                    this.availableTypes
                                        .filter(item => item.name == key)
                                        .map(item => item.min)[0] ?? null,
                                max:
                                    this.availableTypes
                                        .filter(item => item.name == key)
                                        .map(item => item.max)[0] ?? null,
                                period_range:
                                    this.availableTypes
                                        .filter(item => item.name == key)
                                        .map(item => item.period_range)[0] ?? null,
                                collection:
                                    this.availableTypes
                                        .filter(item => item.name == key)
                                        .map(item => item.collection)[0] ?? null,
                                fld_period:
                                    this.availableTypes
                                        .filter(item => item.name == key)
                                        .map(item => item.fld_period)[0] ?? null,
                                default_values: this.availableTypes
                                    .filter(item => item.name == key)
                                    .map(item => item.default_values)[0] ?? [-15, -15],
                            },
                            this.szumDrawer,
                            showOnlySelectedRule, //used, // can be removed if is used
                            {
                                onSave: (key: string, mongoCondition, andOrSelection: string) => {
                                    this.onItemSave(key, mongoCondition, andOrSelection);
                                },
                                onSaveRelatedFields: (key, relatedFields) => {
                                    console.log('onSaveRelatedFields', key, relatedFields);
                                    // this.onItemSave(key, relatedFields);
                                    this.onItemRelatedFieldsSave(key, relatedFields);
                                },
                                onRemove: (key: string) => {
                                    this.onItemRemove(key);
                                },
                                onChangeSubMenu: (key: string) => {
                                    console.log('onChangeSubMenu, populateFilterGroup, item', key);
                                    this.filterEditorItems[key].reload();
                                },
                                onTemporaryHideModal: () => {
                                    console.log('onTemporaryHideModal');
                                    this.hide();
                                    this.callbacks.onTemporaryHide();
                                },
                                onShowModal: () => {
                                    this.show(
                                        document.querySelector('.js-add-filter'),
                                        false,
                                        false
                                    );
                                },
                                onDrawComplete: (key, geojson) => {
                                    this.onDrawComplete(key, geojson);
                                },
                                onDrawCancel: key => {
                                    this.filterEditorItems[key].onClose(this.filter);
                                    this.callbacks.onCancel();
                                },
                                onChangeAnything: (key: string) => {
                                    this.onChangeAnything(key);
                                },
                            }
                        );
                    }
                }
            }
        });
    }

    onItemSave(key: string, mongoCondition: QueryGeometryType | QueryType, andOrSelection: string) {
        console.log('onSave', key, mongoCondition);
        // this.filterLastUsed.updateFilterItem(key.split('/')[0], mongoCondition);
        // this.filterManager.featureInactiveQueries.setActiveQuery(key, true);
        this.filter.andOrSelection = andOrSelection;
        this.filter.updateFilterItem(key.split('/')[0], mongoCondition);
        this.filterLastUsed.updateFilterItem(key.split('/')[0], this.filter.getFilterItem(key));
        this.hide();
        this.clearDirtyStatus();
        this.callbacks.onSave(this.filter, this.filterLastUsed);
    }

    onItemRelatedFieldsSave(key: string, relatedFields: RelationType) {
        this.filter.updateFilterRelatedItem(key, relatedFields);
        this.filterLastUsed.updateFilterRelatedItem(key, relatedFields);
        this.clearDirtyStatus();
        this.hide();
        this.callbacks.onSave(this.filter, this.filterLastUsed);
    }

    onItemRemove(key: string) {
        // console.log('onRemove', key, this.filter.getFilterItem(key));
        // this.filterLastUsed.updateFilterItem(key.split('/')[0], this.filter.getFilterItem(key));// FIXME: add support for geom/radius
        // this.filterManager.featureInactiveQueries.setActiveQuery(key, false);
        // this.filterManager.featureInactiveQueries.removeKeyFromInactiveQueries(key);
        this.filter.removeFilterItem(key.split('/')[0]);
        if (key.split('/')[0] === 'geom') {
            SelectedArea.resetGeometry();
        }
        this.clearDirtyStatus();
        this.hide();
        this.callbacks.onSave(this.filter, this.filterLastUsed);
    }

    onLabelClickConfirmed(key: string, filter: Filter): boolean {
        // console.log('onLabelClick', key, filter.getFilter());

        if (this.dirtyStatus.isChanged) {
            // this.dirtyStatus.key !== key &&
            if (!confirm(_('Changing the filter will discard unsaved data. Continue?'))) {
                return false;
            }
            this.clearDirtyStatus();
        }

        let key_array: string[] = key.split('/');

        // If key is geom/radius, then we need to check if geom/area is used
        if (key_array.length == 2) {
            var other = Object.keys(filter).filter(f => {
                return f == key_array[0];
            });
            if (other.length) {
                switch (other[0]) {
                    case 'geom':
                        let subfilter = filter.getFilterItem('geom')?.properties?.filter ?? 'area';
                        if (subfilter == key_array[1]) {
                            other = [];
                        } else {
                            other = ['geom/' + subfilter];
                        }
                        break;
                }
            }
            if (
                !other.length ||
                confirm(
                    _('Using this filter will disable the filter {filter}. Continue?', {
                        values: { filter: _(other[0]) },
                    })
                )
            ) {
                if (other.length) {
                    filter.removeFilterItem(other[0].split('/')[0]);
                    this.root
                        .querySelector('div[filter_key="' + other[0] + '"]')
                        ?.classList.remove('chosen');
                    if (other[0].split('/')[0] == 'geom') {
                        SelectedArea.resetGeometry();
                    }
                }
                key = key_array.join('/');
            } else {
                return false;
            }
        }

        // If current key is already selected, then we need to close it (but why?)
        if (
            this.selectedKey &&
            this.filterEditorItems[this.selectedKey] &&
            typeof this.filterEditorItems[this.selectedKey].onClose == 'function'
        ) {
            this.filterEditorItems[this.selectedKey].onClose(this.filter);
        }

        // Update current selected key
        this.selectedKey = key;

        // Set all labels as not selected
        this.root.querySelectorAll('.menu_filter_label').forEach(el => {
            el.classList.remove('open');
        });

        return true;
    }

    onDrawComplete(key: string, geojson: GeojsonFieldsType) {
        console.log('onDrawComplete3', key, geojson);
        if (key == 'geom/area') {
            if (!geojson.properties) {
                geojson.properties = {};
            }
            geojson.properties['filter'] = 'area';
            // geojson.properties = { filter: 'area', type: 'Polygon', coordinates: [[[]]] };
        }
        console.log('onDrawComplete3', key, geojson);
        console.log('onDrawComplete3', JSON.stringify(geojson));
        // this.filter.updateFilterItem(key.split('/')[0], (new Filter()).getFilterItemFromGeojson(geojson));
        this.filter.updateFilterItem(
            key.split('/')[0],
            new Filter().getFilterItemFromGeojson(geojson)
        );
        this.filterLastUsed.updateFilterItem(
            key.split('/')[0],
            new Filter().getFilterItemFromGeojson(geojson)
        );
        this.callbacks.onSave(this.filter, this.filterLastUsed);
        // this.root.showModal();
    }

    populateLastUsedFilters(filter_list_title_list: HTMLDivElement) {
        // console.log('populateLastUsedFilters', this.filterLastUsed);

        let itemKeysInThisCategory = this.filterLastUsed.getMenuLabelKeys(6);

        let one_column = document.createElement('div');
        one_column.setAttribute('class', 'filter_group_box filter_group_box_last_used');

        let column_header = document.createElement('div');
        column_header.setAttribute('class', 'menu_filter_column_name');
        let category_translated = _('Twoje filtry');
        column_header.innerHTML =
            category_translated.charAt(0).toUpperCase() + category_translated.slice(1);
        one_column.appendChild(column_header);

        let category_content = document.createElement('div');
        category_content.setAttribute('class', 'menu_filter_group_content last_used_group');

        let group_name = document.createElement('div');
        group_name.setAttribute('class', 'menu_filter_group_name');
        group_name.innerHTML = `<span>${_('Ostatnio używane')}</span>`;
        category_content.appendChild(group_name);

        itemKeysInThisCategory.forEach(attrkey => {
            let filtersForKey;
            if (attrkey == 'geom') {
                filtersForKey = [
                    attrkey +
                        '/' +
                        (this.filterLastUsed.getFilterItem('geom')?.properties?.filter ?? 'area'),
                ];
            } else {
                filtersForKey = this.filter.getMoreKeysForThisKey(attrkey);
            }
            let subMenu: FilterSubMenuValue = this.originalFilter.getSubMenuItems(attrkey);
            for (let i = 0; i < filtersForKey.length; i++) {
                let key = filtersForKey[i];
                let itemLabel = _(this.originalFilter.getNameForItemKey(key));

                // category_content.appendChild(this.filterEditorItems[key].getLabelElement());
                category_content.appendChild(
                    new LabelMenuElement().getLabelMenuElement(
                        key,
                        itemLabel.charAt(0).toUpperCase() + itemLabel.slice(1),
                        false, // this.selectedKey === key,
                        false, // used,
                        false,
                        true,
                        {
                            onLabelClick: (key: string) => {
                                if (this.onLabelClickConfirmed(key, this.filterLastUsed)) {
                                    this.filterEditorItems[key].load(this.filterLastUsed);
                                    this.filterEditorItems[key].reload();
                                    return true;
                                } else {
                                    return false;
                                }
                            },
                            reload: () => {
                                this.filterEditorItems[key].reload();
                            },
                        }
                    )
                );
            }
        });

        let buttonsContainer = document.createElement('div') as HTMLDivElement;
        if (this.showViewButtons) {
            buttonsContainer.setAttribute('class', 'filter_list_buttons_container filter-buttons');

            let loadLastViewButton = document.createElement('button') as HTMLButtonElement;
            loadLastViewButton.setAttribute('class', 'button-filter button-save button-view');
            loadLastViewButton.innerHTML = _('Load view');
            loadLastViewButton.addEventListener('click', () => {
                this.hide();
                this.callbacks.onLoadView();
            });
            buttonsContainer.appendChild(loadLastViewButton);

            let saveLastViewButton = document.createElement('button') as HTMLButtonElement;
            saveLastViewButton.setAttribute('class', 'button-filter button-save button-view');
            saveLastViewButton.innerHTML = _('Save view');
            saveLastViewButton.addEventListener('click', () => {
                this.hide();
                this.callbacks.onSaveView();
            });
            buttonsContainer.appendChild(saveLastViewButton);
        }

        one_column.appendChild(category_content);

        if (this.showViewButtons) {
            one_column.appendChild(buttonsContainer);
        }

        filter_list_title_list?.appendChild(one_column);
    }

    // https://developer.mozilla.org/en-US/docs/Web/HTML/Element/dialog
    getHTML(): string {
        return `
<!--            <dialog style="width:860px; padding: 0; border: 0 solid black; color: black; box-shadow: -1px 11px 31px -10px rgba(0, 0, 0, 0.75);">-->
<!--                <div class="modal-filter-editor">-->
            <div class="main" style="width:860px; padding: 0; border: 0 solid black; color: black; box-shadow: -1px 11px 31px -10px rgba(0, 0, 0, 0.75);">
                    <div class="filter_list_upper_border" style="position: relative; z-index: -2; border-top:1px solid black; height:0;"></div>
                    <div class="container_for_arrows">
                        <div class="container__arrow container__arrow--tl" style="border: 1px solid black;"></div>
                    </div>
                    <div class="filter_list_title_list"></div>
                    <div class="filter_list_columns"></div>
            </div>
<!--                </div>-->
<!--            </dialog>-->
            `;
    }
}

export default ModalFilterEditor;
